import React, { useEffect, useState } from 'react';

const API = '/api';

// ─── Live Device Test (runs directly inside the iOS app WKWebView) ───────────
function LiveDeviceTest() {
  const [isNative, setIsNative] = useState<boolean | null>(null);
  const [platform, setPlatform] = useState('');
  const [logs, setLogs] = useState<string[]>([]);
  const [running, setRunning] = useState(false);

  const addLog = (msg: string) => setLogs(prev => [...prev, `${new Date().toLocaleTimeString()}: ${msg}`]);

  useEffect(() => {
    import('@capacitor/core').then(({ Capacitor }) => {
      setIsNative(Capacitor.isNativePlatform());
      setPlatform(Capacitor.getPlatform());
    }).catch(() => {
      setIsNative(false);
      setPlatform('web (Capacitor not loaded)');
    });
  }, []);

  const runLiveTest = async () => {
    setRunning(true);
    setLogs([]);
    const authToken = localStorage.getItem('authToken');

    try {
      const { Capacitor } = await import('@capacitor/core');
      const plat = Capacitor.getPlatform();
      addLog(`Platform: ${plat}`);
      addLog(`isNativePlatform: ${Capacitor.isNativePlatform()}`);

      if (!Capacitor.isNativePlatform()) {
        addLog('⚠️ NOT running inside native iOS app. Open this page from WITHIN the iOS app.');
        setRunning(false);
        return;
      }

      const { PushNotifications } = await import('@capacitor/push-notifications');

      // Check permissions
      const perm = await PushNotifications.checkPermissions();
      addLog(`Permission status: ${perm.receive}`);

      if (perm.receive === 'prompt') {
        addLog('Requesting permission...');
        const newPerm = await PushNotifications.requestPermissions();
        addLog(`After request: ${newPerm.receive}`);
        if (newPerm.receive !== 'granted') {
          addLog('❌ Permission denied! Go to iOS Settings → Notifications → Enable for this app.');
          setRunning(false);
          return;
        }
      } else if (perm.receive !== 'granted') {
        addLog('❌ Permission DENIED. Go to iOS Settings → Notifications → Enable for this app.');
        setRunning(false);
        return;
      }

      addLog('✅ Permission granted. Registering...');

      await PushNotifications.removeAllListeners();

      const timeout = setTimeout(() => {
        addLog('⏱️ Timeout: registration event never fired after 15s');
        setRunning(false);
      }, 15000);

      PushNotifications.addListener('registrationError', (err: any) => {
        clearTimeout(timeout);
        addLog(`❌ registrationError: ${JSON.stringify(err)}`);
        setRunning(false);
      });

      PushNotifications.addListener('registration', async (apnsData: any) => {
        clearTimeout(timeout);
        const apnsToken = apnsData.value;
        addLog(`✅ APNs token received (${apnsToken.length} chars): ${apnsToken.substring(0, 16)}...`);
        const isApns = /^[0-9A-F]{64}$/i.test(apnsToken.trim());
        addLog(`   Token type: ${isApns ? '⚠️ APNs hex token (64 chars)' : '✅ Looks like FCM token'}`);

        if (plat === 'ios') {
          addLog('Trying FCM.getToken() (up to 5 attempts)...');
          let fcmToken = '';
          for (let i = 1; i <= 5; i++) {
            try {
              const { FCM } = await import('@capacitor-community/fcm');
              const result = await FCM.getToken();
              if (result?.token && result.token.length > 50) {
                fcmToken = result.token;
                addLog(`✅ FCM token obtained on attempt ${i}: ${fcmToken.substring(0, 20)}...`);
                break;
              } else {
                addLog(`   Attempt ${i}: token too short (${result?.token?.length || 0} chars)`);
              }
            } catch (e: any) {
              addLog(`   Attempt ${i} error: ${e?.message || String(e)}`);
            }
            if (i < 5) {
              const delay = Math.pow(2, i - 1) * 1000;
              addLog(`   Waiting ${delay}ms...`);
              await new Promise(r => setTimeout(r, delay));
            }
          }

          const tokenToSave = fcmToken || apnsToken;
          addLog(`Saving ${fcmToken ? 'FCM' : 'APNs'} token to backend...`);

          if (!authToken) {
            addLog('❌ No authToken in localStorage! Cannot save token. Please log in first.');
            setRunning(false);
            return;
          }

          try {
            const resp = await fetch('https://app.phanh.app/api/auth/fcm-token', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
              body: JSON.stringify({ token: tokenToSave, platform: 'ios' })
            });
            const body = await resp.text();
            addLog(`Backend: HTTP ${resp.status} → ${body.substring(0, 80)}`);
            if (resp.ok) addLog('✅ Token saved! Refresh the push-debug page on your computer.');
            else addLog('❌ Backend returned error. Check the response above.');
          } catch (e: any) {
            addLog(`❌ Network error saving token: ${e?.message}`);
          }
        }

        setRunning(false);
      });

      await PushNotifications.register();
      addLog('register() called. Waiting for registration event...');

    } catch (e: any) {
      addLog(`❌ Fatal error: ${e?.message || String(e)}`);
      setRunning(false);
    }
  };

  const bg = isNative === null ? '#f1f5f9' : isNative ? '#f0fdf4' : '#fffbeb';
  const border = isNative === null ? '#cbd5e1' : isNative ? '#86efac' : '#fcd34d';

  return (
    <div style={{ border: `2px solid ${border}`, borderRadius: 8, background: bg, padding: 16, marginBottom: 24 }}>
      <h3 style={{ margin: '0 0 8px', color: '#1e293b', fontSize: 15 }}>
        📱 Live Device Test
        {isNative === true && <span style={{ marginLeft: 8, background: '#16a34a', color: 'white', padding: '2px 8px', borderRadius: 4, fontSize: 11 }}>INSIDE iOS APP ✅</span>}
        {isNative === false && <span style={{ marginLeft: 8, background: '#d97706', color: 'white', padding: '2px 8px', borderRadius: 4, fontSize: 11 }}>BROWSER (not iOS app)</span>}
      </h3>
      <p style={{ fontSize: 12, color: '#64748b', margin: '0 0 12px' }}>
        {isNative
          ? `Running inside native app (${platform}). Click Run Test to check push registration live.`
          : `You are viewing this page in a browser (${platform}). To use Live Test, navigate to this URL from INSIDE the iOS app.`
        }
      </p>
      {isNative === false && (
        <div style={{ background: '#fef3c7', border: '1px solid #f59e0b', borderRadius: 6, padding: 10, fontSize: 12, marginBottom: 12 }}>
          <b>👉 How to use Live Test from iOS app:</b><br/>
          1. Open the iOS app on your iPhone<br/>
          2. Go to any page, then in the URL... <br/>
          <b>Easier:</b> Add a link to this page in the app sidebar (see below)
        </div>
      )}
      <button
        onClick={runLiveTest}
        disabled={running}
        style={{ background: isNative ? '#16a34a' : '#6b7280', color: 'white', border: 'none', borderRadius: 6, padding: '8px 16px', fontWeight: 'bold', cursor: running ? 'not-allowed' : 'pointer', marginBottom: 12 }}
      >
        {running ? '⏳ Running...' : '▶️ Run Live Push Test'}
      </button>
      {logs.length > 0 && (
        <pre style={{ background: '#0f172a', color: '#94a3b8', padding: 12, borderRadius: 6, fontSize: 11, maxHeight: 350, overflow: 'auto', whiteSpace: 'pre-wrap', margin: 0 }}>
          {logs.join('\n')}
        </pre>
      )}
    </div>
  );
}

export default function PushDebug() {
  const [result, setResult] = useState<any>(null);
  const [testResult, setTestResult] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [testLoading, setTestLoading] = useState(false);
  const [clearLoading, setClearLoading] = useState(false);
  const [resetLogsLoading, setResetLogsLoading] = useState(false);
  const [error, setError] = useState('');

  const token = localStorage.getItem('authToken');

  const loadDebug = () => {
    if (!token) {
      setError('No authToken in localStorage. Please log in first.');
      setLoading(false);
      return;
    }
    setLoading(true);
    fetch(`${API}/notifications/debug-push`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => r.json())
      .then(data => { setResult(data); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  };

  useEffect(() => { loadDebug(); }, []);

  const sendTestPush = async () => {
    setTestLoading(true);
    setTestResult(null);
    try {
      const r = await fetch(`${API}/notifications/test`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: '🔔 Test Push', message: 'Test from debug page — ' + new Date().toLocaleTimeString() })
      });
      setTestResult(await r.json());
    } catch (e: any) {
      setTestResult({ error: e.message });
    }
    setTestLoading(false);
  };

  const clearTokens = async () => {
    if (!confirm('Xóa hết FCM tokens? Bạn sẽ cần mở lại app iOS và đăng nhập lại.')) return;
    setClearLoading(true);
    try {
      const r = await fetch(`${API}/notifications/tokens-all`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await r.json();
      alert(data.message);
      loadDebug();
    } catch (e: any) {
      alert('Error: ' + e.message);
    }
    setClearLoading(false);
  };

  const resetDeviceLogs = async () => {
    setResetLogsLoading(true);
    try {
      // Clear deviceDebug by posting empty/reset data
      await fetch(`${API}/notifications/report-device-status`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ permissionStatus: null, platform: null, apnsToken: null, fcmToken: null, fcmError: null, backendStatus: 'MANUALLY_RESET', logs: [] })
      });
      alert('Device debug logs cleared. Now open iOS app and log in to generate fresh logs.');
      loadDebug();
    } catch (e: any) {
      alert('Error: ' + e.message);
    }
    setResetLogsLoading(false);
  };

  const d = result?.debug;

  return (
    <div style={{ padding: 24, fontFamily: 'monospace', maxWidth: 750, margin: '0 auto' }}>
      <h2 style={{ color: '#1e293b' }}>🔔 Push Notification Debug</h2>

      {/* Live Device Test — shows results when accessed from inside iOS app */}
      <LiveDeviceTest />

      {loading && <p>⏳ Loading...</p>}
      {error && <p style={{ color: 'red' }}>❌ Error: {error}</p>}

      {d && (
        <div>
          {/* Firebase */}
          <Section title="1. Firebase Admin SDK">
            <Row label="Initialized" value={d.firebaseInitialized} bool />
          </Section>

          {/* Device Debug Logs (remote logs from iOS app) */}
          {d?.deviceDebug && (
            <Section title="🔍 Device Debug Logs (from iOS app)">
              <div style={{ fontSize: 12, marginBottom: 8 }}>
                <Row label="Last seen" value={d.deviceDebug.lastSeen ? new Date(d.deviceDebug.lastSeen).toLocaleString() : '(no data)'} />
                <Row label="Platform" value={d.deviceDebug.platform || '(no data)'} />
                <div style={{ display: 'flex', gap: 12, marginBottom: 4 }}>
                  <span style={{ color: '#64748b', minWidth: 200 }}>Permission:</span>
                  <span style={{ fontWeight: 'bold', color: d.deviceDebug.permissionStatus === 'granted' ? 'green' : d.deviceDebug.permissionStatus ? '#dc2626' : '#64748b' }}>
                    {d.deviceDebug.permissionStatus === 'granted' ? '✅ granted' : d.deviceDebug.permissionStatus ? `❌ ${d.deviceDebug.permissionStatus}` : '(no data yet)'}
                  </span>
                </div>
                <Row label="APNs token" value={d.deviceDebug.apnsToken || '❌ Not received'} />
                <Row label="FCM token" value={d.deviceDebug.fcmToken || '❌ Not obtained'} />
                {d.deviceDebug.fcmError && (
                  <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 6, padding: 8, marginTop: 6 }}>
                    <b style={{ color: 'red' }}>❌ FCM Error:</b> {d.deviceDebug.fcmError}
                  </div>
                )}
                {d.deviceDebug.backendStatus && (
                  <div style={{ marginTop: 4 }}>
                    <b>Backend status:</b> <code>{d.deviceDebug.backendStatus}</code>
                  </div>
                )}
              </div>
              {d.deviceDebug.logs?.length > 0 && (
                <pre style={{ background: '#0f172a', color: '#94a3b8', padding: 12, borderRadius: 6, fontSize: 11, maxHeight: 300, overflow: 'auto', whiteSpace: 'pre-wrap' }}>
                  {d.deviceDebug.logs.join('\n')}
                </pre>
              )}
              {(!d.deviceDebug.logs || d.deviceDebug.logs.length === 0) && (
                <p style={{ color: '#64748b', fontSize: 12 }}>
                  ⚠️ No step-by-step logs yet. The data above may be stale.<br/>
                  Click "Reset" below, then open the iOS app (new build) and log in.
                </p>
              )}
              <button
                onClick={resetDeviceLogs}
                disabled={resetLogsLoading}
                style={{ marginTop: 8, background: '#64748b', color: 'white', border: 'none', borderRadius: 6, padding: '6px 14px', fontSize: 12, cursor: 'pointer' }}
              >
                {resetLogsLoading ? '⏳ Resetting...' : '🔄 Reset Device Logs'}
              </button>
            </Section>
          )}

          {!d?.deviceDebug && d && (
            <Section title="🔍 Device Debug Logs (from iOS app)">
              <p style={{ color: 'orange', fontSize: 13 }}>
                ⚠️ No device logs yet. This means the iOS app has never successfully reported its push status to the server.<br/>
                Build the new version in Codemagic, install on iPhone, log in — then refresh this page.
              </p>
            </Section>
          )}

          {/* FCM Tokens */}
          <Section title="2. FCM Tokens saved for your account">
            <Row label="Token count" value={d.fcmTokensCount} warn={d.fcmTokensCount === 0} />
            {d.fcmTokensCount === 0 && (
              <p style={{ color: 'orange' }}>
                ⚠️ No tokens! Open the iOS app and log in to register a token.
              </p>
            )}
            {d.fcmTokens?.map((t: any, i: number) => {
              const isApns = /^[0-9A-F]{8,}/i.test(t.tokenPrefix?.replace('...', ''));
              return (
                <div key={i} style={{
                  background: isApns ? '#fff3cd' : '#f1f5f9',
                  border: isApns ? '2px solid #f59e0b' : '1px solid #e2e8f0',
                  borderRadius: 6, padding: 10, marginBottom: 6
                }}>
                  <b>Token {i + 1}:</b> {t.tokenPrefix}<br />
                  Platform: <b>{t.platform}</b> | Added: {new Date(t.addedAt).toLocaleString()}<br />
                  {isApns ? (
                    <span style={{ color: '#b45309', fontWeight: 'bold' }}>
                      ⚠️ LIKELY APNs TOKEN (hex format) — NOT an FCM token! Firebase CANNOT use this. 
                      Click "Clear Old Tokens" below, then reopen iOS app.
                    </span>
                  ) : (
                    <span style={{ color: 'green' }}>✅ Looks like a valid FCM token</span>
                  )}
                </div>
              );
            })}
            <button
              onClick={clearTokens}
              disabled={clearLoading}
              style={{ marginTop: 8, background: '#dc2626', color: 'white', border: 'none', borderRadius: 6, padding: '8px 16px', fontSize: 13, cursor: 'pointer' }}
            >
              {clearLoading ? '⏳ Clearing...' : '🗑️ Clear Old Tokens (Force Re-register)'}
            </button>
          </Section>

          {/* Business Checks */}
          <Section title="3. Admin query (who would receive notifications)">
            {d.businessChecks?.map((b: any, i: number) => (
              <div key={i} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, padding: 10, marginBottom: 8 }}>
                <b>Business:</b> {b.businessId}<br />
                <b>Your role:</b> {b.myRole}<br />
                <b>Admins found:</b>{' '}
                {b.adminsFound.length === 0
                  ? <span style={{ color: 'red' }}>❌ NONE — notifications won't be sent!</span>
                  : b.adminsFound.map((u: any, j: number) => (
                    <span key={j} style={{ color: 'green', marginRight: 8 }}>
                      ✅ {u.email} (tokens: {u.fcmTokenCount})
                    </span>
                  ))
                }
              </div>
            ))}
          </Section>

          {/* Preferences */}
          <Section title="4. Your notification preferences">
            {Object.entries(d.notificationPreferences || {}).filter(([k]) => k !== '_id').map(([k, v]: any) => (
              <Row key={k} label={k} value={v} bool />
            ))}
          </Section>
        </div>
      )}

      {/* Test Send */}
      <Section title="5. Send test push (with detailed FCM result)">
        <button
          onClick={sendTestPush}
          disabled={testLoading}
          style={{ background: '#3b82f6', color: 'white', border: 'none', borderRadius: 6, padding: '10px 20px', fontSize: 14, cursor: 'pointer' }}
        >
          {testLoading ? '⏳ Sending...' : '🚀 Send Test Push Now'}
        </button>

        {testResult && (
          <div style={{ marginTop: 10 }}>
            {/* Summary */}
            <div style={{
              padding: 10, borderRadius: 6, marginBottom: 8,
              background: testResult.fcmSuccessCount > 0 ? '#dcfce7' : '#fee2e2',
              border: `1px solid ${testResult.fcmSuccessCount > 0 ? '#16a34a' : '#dc2626'}`
            }}>
              {testResult.fcmSuccessCount > 0
                ? `✅ FCM sent successfully to ${testResult.fcmSuccessCount} device(s)! Check your iPhone.`
                : testResult.fcmFailureCount !== undefined
                  ? `❌ FCM failed to deliver to any device (${testResult.fcmFailureCount} failure(s))`
                  : `❌ Server error when calling Firebase — see error details below`
              }
            </div>

            {/* Server-side exception (500) — show prominently */}
            {testResult.error && (
              <div style={{ background: '#fff1f2', border: '2px solid #fb7185', borderRadius: 6, padding: 10, marginBottom: 8, fontSize: 12 }}>
                <b style={{ color: '#be123c' }}>🔥 Firebase Server Error:</b><br/>
                <code style={{ color: '#be123c' }}>{testResult.error}</code>
                {testResult.errorCode && <><br/><b>Code:</b> <code>{testResult.errorCode}</code></>}
                {testResult.errorDetails && <><br/><b>Details:</b> <code>{JSON.stringify(testResult.errorDetails)}</code></>}
              </div>
            )}

            {/* Per-token results */}
            {testResult.results?.map((r: any, i: number) => (
              <div key={i} style={{
                background: r.success ? '#f0fdf4' : '#fef2f2',
                border: `1px solid ${r.success ? '#86efac' : '#fca5a5'}`,
                borderRadius: 6, padding: 8, marginBottom: 6, fontSize: 12
              }}>
                <b>Token {i + 1}:</b> {r.tokenPrefix}<br />
                {r.success
                  ? <span style={{ color: 'green' }}>✅ SUCCESS — messageId: {r.messageId}</span>
                  : <span style={{ color: 'red' }}>❌ FAILED — {r.errorCode}: {r.errorMessage}</span>
                }
                {r.errorCode === 'messaging/invalid-registration-token' && (
                  <div style={{ color: '#b45309', marginTop: 4 }}>
                    ⚠️ This is an APNs token, not an FCM token. Click "Clear Old Tokens" above, then reopen iOS app.
                  </div>
                )}
              </div>
            ))}

            {testResult.tokenDiag?.length > 0 && (
              <details style={{ marginTop: 8 }}>
                <summary style={{ cursor: 'pointer', color: '#64748b' }}>Token diagnosis details</summary>
                <pre style={{ background: '#1e293b', color: '#94a3b8', padding: 10, borderRadius: 6, fontSize: 11, marginTop: 4 }}>
                  {JSON.stringify(testResult.tokenDiag, null, 2)}
                </pre>
              </details>
            )}

            <details style={{ marginTop: 8 }} open={!!testResult.error}>
              <summary style={{ cursor: 'pointer', color: '#64748b' }}>Raw JSON response</summary>
              <pre style={{ background: '#1e293b', color: '#94a3b8', padding: 10, borderRadius: 6, fontSize: 11, marginTop: 4 }}>
                {JSON.stringify(testResult, null, 2)}
              </pre>
            </details>
          </div>
        )}
      </Section>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 20, border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
      <div style={{ background: '#1e293b', color: 'white', padding: '8px 12px', fontWeight: 'bold', fontSize: 13 }}>
        {title}
      </div>
      <div style={{ padding: 12 }}>{children}</div>
    </div>
  );
}

function Row({ label, value, bool, warn }: { label: string; value: any; bool?: boolean; warn?: boolean }) {
  const display = bool ? (value ? '✅ YES' : '❌ NO') : String(value);
  const color = bool ? (value ? 'green' : 'red') : (warn ? 'orange' : '#1e293b');
  return (
    <div style={{ display: 'flex', gap: 12, marginBottom: 4 }}>
      <span style={{ color: '#64748b', minWidth: 200 }}>{label}:</span>
      <span style={{ color, fontWeight: 'bold' }}>{display}</span>
    </div>
  );
}
